import { computed, ref } from "vue";
import type { GameState } from "../../core/gameState.js";
import type { MatchStatEvent, ReplayAction } from "../../core/tournament.js";

/**
 * Tracks the game the user is currently playing so that leaving the page mid-
 * match surfaces a prominent "Resume game" button in the top bar.
 *
 * - Online (vs another human): the game lives on the server, so only the game
 *   id is tracked — persisted in localStorage to survive reloads.
 * - Tournament matches (vs the AI): the live GameState exists only in the
 *   browser, so leaving the page parks a full snapshot here — mirrored to
 *   sessionStorage, so a refresh resumes the match as the player instead of
 *   losing it (a refreshed player used to be left with only their own
 *   spectator link to watch the game they had been playing).
 * - Plain local games (vs Sherlock or hotseat): parked the same way, so
 *   starting "Play → Sherlock AI" again resumes the running match instead of
 *   silently abandoning it.
 *
 * sessionStorage rather than localStorage on purpose: it is per-tab, so a
 * refresh keeps the match while a second tab cannot steal it.
 */

const ONLINE_KEY = "rasenschach.activeOnlineGame";
const ONLINE_WAITING_KEY = "rasenschach.activeOnlineGameWaiting";
const TOURNAMENT_PARK_KEY = "rasenschach.parkedTournamentMatch";
const LOCAL_PARK_KEY = "rasenschach.parkedLocalGame";

function loadParked<T>(key: string): T | null {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) as T : null;
  } catch {
    return null;
  }
}

function persistParked(key: string, value: unknown | null) {
  try {
    if (value) sessionStorage.setItem(key, JSON.stringify(value));
    else sessionStorage.removeItem(key);
  } catch { /* storage unavailable — in-memory parking still works */ }
}

type HistorySnapshot = Parameters<typeof GameState.fromSnapshot>[0];

export interface ParkedTournamentMatch {
  matchId: string;
  myTeam: "A" | "B";
  snapshot: HistorySnapshot;
  moveLog: unknown[];
  localMoves: unknown[];
  tournamentEvents: MatchStatEvent[];
  tournamentReplayActions: ReplayAction[];
  /**
   * Live spectator-share credentials, carried across park/resume so the match
   * keeps the same shareable link when the player leaves and comes back.
   */
  liveShareId?: string;
  liveShareKey?: string;
  liveShareUrl?: string;
}

export interface ParkedLocalGame {
  /** Route that resumes the match (e.g. "/local?ai=sherlock&strength=hard"). */
  route: string;
  /** The page's AI mode; a fresh /local visit resumes only a matching mode. */
  aiMode: string;
  aiStrength: string;
  myTeam: "A" | "B";
  snapshot: HistorySnapshot;
  moveLog: unknown[];
  localMoves: unknown[];
}

function loadOnlineId(): string | null {
  try {
    return localStorage.getItem(ONLINE_KEY);
  } catch {
    return null;
  }
}

function loadOnlineWaiting(): boolean {
  try {
    return localStorage.getItem(ONLINE_WAITING_KEY) === "1";
  } catch {
    return false;
  }
}

const activeOnlineGameId = ref<string | null>(loadOnlineId());
/** True while that game is still an open invitation nobody has accepted. */
const activeOnlineGameWaiting = ref<boolean>(loadOnlineWaiting());
const parkedTournamentMatch = ref<ParkedTournamentMatch | null>(loadParked(TOURNAMENT_PARK_KEY));
const parkedLocalGame = ref<ParkedLocalGame | null>(loadParked(LOCAL_PARK_KEY));

function persistOnlineId(id: string | null) {
  try {
    if (id) localStorage.setItem(ONLINE_KEY, id);
    else localStorage.removeItem(ONLINE_KEY);
  } catch { /* storage unavailable — in-memory tracking still works */ }
}

function persistOnlineWaiting(waiting: boolean) {
  try {
    if (waiting) localStorage.setItem(ONLINE_WAITING_KEY, "1");
    else localStorage.removeItem(ONLINE_WAITING_KEY);
  } catch { /* storage unavailable — in-memory tracking still works */ }
}

export function useActiveGame() {
  /**
   * @param options.waiting the game exists but has no opponent yet, so the top
   *   bar offers it as "waiting for opponent" instead of "resume game".
   */
  function setOnlineGame(gameId: string, options: { waiting?: boolean } = {}) {
    activeOnlineGameId.value = gameId;
    persistOnlineId(gameId);
    const waiting = options.waiting === true;
    activeOnlineGameWaiting.value = waiting;
    persistOnlineWaiting(waiting);
  }

  function clearOnlineGame(gameId?: string) {
    if (gameId && activeOnlineGameId.value !== gameId) return;
    activeOnlineGameId.value = null;
    persistOnlineId(null);
    activeOnlineGameWaiting.value = false;
    persistOnlineWaiting(false);
  }

  function parkTournamentMatch(parked: ParkedTournamentMatch) {
    parkedTournamentMatch.value = parked;
    persistParked(TOURNAMENT_PARK_KEY, parked);
  }

  /** Claims the parked match for the given fixture, removing it from the shelf. */
  function takeParkedTournamentMatch(matchId: string): ParkedTournamentMatch | null {
    const parked = parkedTournamentMatch.value;
    if (!parked || parked.matchId !== matchId) return null;
    parkedTournamentMatch.value = null;
    persistParked(TOURNAMENT_PARK_KEY, null);
    return parked;
  }

  function clearParkedTournamentMatch() {
    parkedTournamentMatch.value = null;
    persistParked(TOURNAMENT_PARK_KEY, null);
  }

  const hasParkedTournamentMatch = computed(() => parkedTournamentMatch.value !== null);

  function parkLocalGame(parked: ParkedLocalGame) {
    parkedLocalGame.value = parked;
    persistParked(LOCAL_PARK_KEY, parked);
  }

  /** Claims the parked local game for the given AI mode, removing it from the shelf. */
  function takeParkedLocalGame(aiMode: string): ParkedLocalGame | null {
    const parked = parkedLocalGame.value;
    if (!parked || parked.aiMode !== aiMode) return null;
    parkedLocalGame.value = null;
    persistParked(LOCAL_PARK_KEY, null);
    return parked;
  }

  const parkedLocalGameRoute = computed(() => parkedLocalGame.value?.route ?? null);

  return {
    activeOnlineGameId,
    activeOnlineGameWaiting,
    hasParkedTournamentMatch,
    parkedLocalGameRoute,
    setOnlineGame,
    clearOnlineGame,
    parkTournamentMatch,
    takeParkedTournamentMatch,
    clearParkedTournamentMatch,
    parkLocalGame,
    takeParkedLocalGame,
  };
}
